import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Spinner } from "../../components/Brand";

const EMPTY = { sku: "", name: "", description: "", category: "", unit: "pc", unit_price: 0, stock_qty: 0, active: true };

export default function AdminParts() {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [q, setQ] = useState("");
    const [category, setCategory] = useState("");
    const [activeOnly, setActiveOnly] = useState(true);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: "100" });
            if (q) params.set("q", q);
            if (category) params.set("category", category);
            if (activeOnly) params.set("active", "true");
            const { data } = await api.get(`/parts?${params}`);
            setItems(data.items || []);
            setCategories(data.categories || []);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, [q, category, activeOnly]);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => { setForm(EMPTY); setEditingId(null); setShowForm(true); };
    const openEdit = (p) => {
        setForm({ ...EMPTY, ...p });
        setEditingId(p.id);
        setShowForm(true);
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form, unit_price: +form.unit_price, stock_qty: +form.stock_qty };
            if (editingId) {
                await api.patch(`/parts/${editingId}`, payload);
                toast.success("Part updated");
            } else {
                await api.post("/parts", payload);
                toast.success("Part created");
            }
            setShowForm(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Deactivate part "${p.sku}"?`)) return;
        try {
            await api.delete(`/parts/${p.id}`);
            toast.success("Part deactivated");
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="lx-label text-[#D4AF37] mb-2">Inventory</div>
                    <h1 className="font-serif text-4xl text-white">Parts</h1>
                    <p className="text-xs text-white/40 mt-1">Master list of spare parts &amp; consumables</p>
                </div>
                <button
                    data-testid="parts-new-btn"
                    onClick={openCreate}
                    className="lx-btn-gold px-5 py-2.5 text-sm"
                >
                    + New Part
                </button>
            </div>

            <div className="flex gap-3 flex-wrap items-center">
                <input
                    data-testid="parts-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search SKU, name, description…"
                    className="flex-1 min-w-[240px] bg-[#0F0614] border border-[#2B1438] px-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37]"
                />
                <select
                    data-testid="parts-cat-filter"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="bg-[#0F0614] border border-[#2B1438] px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37]"
                >
                    <option value="">All categories</option>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <label className="flex items-center gap-2 text-xs text-white/70">
                    <input
                        type="checkbox"
                        checked={activeOnly}
                        onChange={(e) => setActiveOnly(e.target.checked)}
                        data-testid="parts-active-only"
                    />
                    Active only
                </label>
            </div>

            <div className="lx-card-dark overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-white/40 uppercase tracking-wider text-[10px] border-b border-[#2B1438]">
                                <th className="text-left py-3 px-4">SKU</th>
                                <th className="text-left py-3 px-4">Name</th>
                                <th className="text-left py-3 px-4">Category</th>
                                <th className="text-right py-3 px-4">Unit Price</th>
                                <th className="text-right py-3 px-4">Stock</th>
                                <th className="text-center py-3 px-4">Status</th>
                                <th className="text-right py-3 px-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (<tr><td colSpan={7} className="text-center py-12"><Spinner className="mx-auto" /></td></tr>)}
                            {!loading && items.length === 0 && (
                                <tr><td colSpan={7} className="text-center text-white/40 py-12">No parts yet. Create your first one.</td></tr>
                            )}
                            {items.map((p) => (
                                <tr key={p.id} data-testid={`part-row-${p.id}`} className="border-t border-[#2B1438]/60 hover:bg-[#180A21]/60">
                                    <td className="py-3 px-4 text-white font-mono">{p.sku}</td>
                                    <td className="py-3 px-4 text-white">
                                        {p.name}
                                        {p.description && <div className="text-[10px] text-white/40">{p.description}</div>}
                                    </td>
                                    <td className="py-3 px-4 text-white/70">{p.category || "—"}</td>
                                    <td className="py-3 px-4 text-white text-right tabular-nums">AED {(+p.unit_price).toFixed(2)} <span className="text-white/40 text-[10px]">/ {p.unit}</span></td>
                                    <td className="py-3 px-4 text-white/70 text-right tabular-nums">{p.stock_qty}</td>
                                    <td className="py-3 px-4 text-center">
                                        <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-wider border ${p.active ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-white/10 text-white/40 border-white/20"}`}>
                                            {p.active ? "Active" : "Archived"}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 text-right">
                                        <button data-testid={`part-edit-${p.id}`} onClick={() => openEdit(p)} className="text-[#D4AF37] hover:underline text-xs mr-3">Edit</button>
                                        {p.active && (
                                            <button data-testid={`part-del-${p.id}`} onClick={() => remove(p)} className="text-rose-400 hover:underline text-xs">Deactivate</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {showForm && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
                    <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-[#180A21] border border-[#2B1438] w-full max-w-lg p-6 space-y-4" data-testid="part-form-modal">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="font-serif text-2xl text-white">{editingId ? "Edit Part" : "New Part"}</h3>
                            <button type="button" onClick={() => setShowForm(false)} className="text-white/40 hover:text-white text-2xl leading-none">×</button>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="SKU *">
                                <input required data-testid="part-sku" value={form.sku} disabled={!!editingId} onChange={(e) => setForm({ ...form, sku: e.target.value.toUpperCase() })} className="lx-in" placeholder="e.g. AC-FLT-001" />
                            </Field>
                            <Field label="Category">
                                <input data-testid="part-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="lx-in" placeholder="AC / Plumbing / …" />
                            </Field>
                        </div>
                        <Field label="Name *">
                            <input required data-testid="part-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="lx-in" />
                        </Field>
                        <Field label="Description">
                            <textarea rows={2} data-testid="part-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="lx-in resize-none" />
                        </Field>
                        <div className="grid grid-cols-3 gap-4">
                            <Field label="Unit">
                                <select data-testid="part-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="lx-in">
                                    {["pc", "box", "set", "m", "kg", "L", "hr"].map((u) => <option key={u} value={u}>{u}</option>)}
                                </select>
                            </Field>
                            <Field label="Unit Price (AED) *">
                                <input required type="number" min="0" step="0.01" data-testid="part-price" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} className="lx-in" />
                            </Field>
                            <Field label="Stock Qty">
                                <input type="number" min="0" step="1" data-testid="part-stock" value={form.stock_qty} onChange={(e) => setForm({ ...form, stock_qty: e.target.value })} className="lx-in" />
                            </Field>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-xs uppercase tracking-wider border border-[#2B1438] text-white/70">Cancel</button>
                            <button type="submit" data-testid="part-save-btn" disabled={saving} className="lx-btn-gold px-5 py-2 text-sm disabled:opacity-60">
                                {saving ? <Spinner /> : editingId ? "Update Part" : "Create Part"}
                            </button>
                        </div>
                    </form>
                    <style>{`.lx-in{width:100%;padding:0.6rem 0.75rem;background:#0F0614;border:1px solid #2B1438;color:white;font-size:.875rem;outline:none}.lx-in:focus{border-color:#D4AF37}`}</style>
                </div>
            )}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="lx-label text-white/50 mb-1.5">{label}</div>
            {children}
        </label>
    );
}
